import { effect, inject, Injectable, signal } from '@angular/core';
import SockJS from 'sockjs-client';
import { environment } from '@environment/environment';
import Stomp, { Client, Subscription } from 'stompjs';
import { AuthStore } from '@app/feature/authentication/store/auth.store';
import { NotificationService } from '@app/notification';
import { AuthenticationService } from '@app/feature/authentication/service/authentication.service';
import {
  NotificationApiService,
  NotificationDTO,
} from '@app/notification/services/notification-api.service';

/**
 * Interface correspondant au WebSocketNotificationDTO du backend Spring Boot
 */
export interface WebSocketNotification {
  notificationId: string;
  type: string;
  title: string;
  message: string;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  relatedEntityId?: string;
  relatedEntityType?: string;
  data?: any;
  timestamp: string;
  action?: string;
  read?: boolean;
}

/**
 * Service WebSocket pour Alienala - Adapté au backend Spring Boot
 *
 * Configuration backend:
 * - Endpoint: /ws (avec SockJS)
 * - User destination: /user/{userId}/notifications
 * - Broker: SimpleBroker avec prefix /user
 */
@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private authStore = inject(AuthStore);
  private notificationService = inject(NotificationService);
  private authService = inject(AuthenticationService);
  private notificationApiService = inject(NotificationApiService);

  private stompClient: Client | null = null;
  private notificationSubscription: Subscription | null = null;
  private user = this.authStore.user;

  private connectionState = signal(false);
  private connecting = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private observedRefreshedToken: string | null = null;

  constructor() {
    effect(() => {
      const user = this.user();
      const refreshedToken = this.authService.refreshedAccessToken();
      if (
        user &&
        refreshedToken &&
        refreshedToken !== this.observedRefreshedToken
      ) {
        this.observedRefreshedToken = refreshedToken;
        this.reconnectWithCurrentSession();
      } else if (user) {
        this.connect();
      } else {
        this.disconnect();
      }
    });
  }

  /**
   * Établit la connexion WebSocket avec le backend
   */
  connect(): void {
    if (this.connectionState() || this.connecting) {
      console.log('🔌 [Alienala WebSocket] Already connected.');
      return;
    }

    const user = this.user();
    if (!user?.id) {
      console.warn(
        '⚠️ [Alienala WebSocket] No user ID available, cannot connect',
      );
      return;
    }

    const token = this.authService.getToken();
    if (!token) {
      console.warn(
        '⚠️ [Alienala WebSocket] No authentication token available, cannot connect',
      );
      return;
    }

    console.log('🔌 [Alienala WebSocket] Connecting to:', environment.wsUrl);
    this.connecting = true;

    const socket = new SockJS(environment.wsUrl);
    this.stompClient = Stomp.over(socket);

    // Désactiver les logs de debug STOMP en production
    if (!environment.debug) {
      this.stompClient.debug = () => {};
    }

    const headers = {
      Authorization: 'Bearer ' + token,
    };

    this.stompClient.connect(
      headers,
      () => this.onConnected(),
      (error: any) => this.onError(error),
    );
  }

  /**
   * Callback appelé quand la connexion WebSocket est établie
   */
  private onConnected(): void {
    console.log('✅ [Alienala WebSocket] Connected successfully');
    this.connecting = false;
    this.connectionState.set(true);
    this.clearReconnectTimer();
    this.synchronizeMissedNotifications();

    const destination = `/user/queue/notifications`;
    console.log(`📡 [Alienala WebSocket] Subscribing to: ${destination}`);

    this.notificationSubscription = this.stompClient!.subscribe(
      destination,
      (message) => {
        try {
          const wsNotification: WebSocketNotification = JSON.parse(
            message.body,
          );
          console.log(
            '📬 [Alienala WebSocket] Notification received:',
            wsNotification,
          );
          this.notificationService.handleWebSocketNotification(wsNotification);
        } catch (error) {
          console.error(
            '❌ [Alienala WebSocket] Error parsing notification:',
            error,
          );
        }
      },
    );
  }

  /**
   * Callback appelé en cas d'erreur de connexion
   */
  private onError(error: any): void {
    console.error('❌ [Alienala WebSocket] Connection error:', error);
    this.connecting = false;
    this.connectionState.set(false);

    this.clearReconnectTimer();
    if (
      this.isAuthenticationError(error) &&
      this.authService.hasSessionHint()
    ) {
      this.authService.refreshToken().subscribe({
        next: () => this.connect(),
        error: () => this.disconnect(),
      });
      return;
    }
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      console.log('🔄 [Alienala WebSocket] Attempting to reconnect...');
      this.connect();
    }, 5000);
  }

  private reconnectWithCurrentSession(): void {
    this.clearReconnectTimer();
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
      this.notificationSubscription = null;
    }
    if (this.stompClient?.connected) {
      this.stompClient.disconnect(() => {
        this.connectionState.set(false);
        this.connecting = false;
        this.connect();
      });
      return;
    }
    this.connectionState.set(false);
    this.connecting = false;
    this.connect();
  }

  private isAuthenticationError(error: any): boolean {
    const code = error?.headers?.['error-code'];
    return (
      code === 'SESSION_EXPIRED' ||
      code === 'AUTH_INVALID_TOKEN' ||
      code === 'AUTH_FAILED'
    );
  }

  /**
   * Déconnecte le client WebSocket
   */
  disconnect(): void {
    this.connecting = false;
    this.clearReconnectTimer();
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
      this.notificationSubscription = null;
    }
    if (this.stompClient?.connected) {
      this.stompClient.disconnect(() => {
        console.log('🔌 [Alienala WebSocket] Disconnected');
        this.connectionState.set(false);
      });
    } else {
      this.connectionState.set(false);
    }
  }

  private synchronizeMissedNotifications(): void {
    this.notificationApiService.getNotifications(0, 100).subscribe({
      next: (page) => {
        this.notificationService.synchronizeNotifications(
          page.content.map((notification) =>
            this.toWebSocketNotification(notification),
          ),
        );
      },
      error: (error) => {
        console.error(
          '❌ [Alienala WebSocket] Unable to synchronize notifications:',
          error,
        );
      },
    });
  }

  private toWebSocketNotification(
    notification: NotificationDTO,
  ): WebSocketNotification {
    return {
      notificationId: notification.id,
      type: notification.type,
      title: notification.title,
      message: notification.message,
      priority: notification.priority as WebSocketNotification['priority'],
      relatedEntityId: notification.relatedEntityId,
      relatedEntityType: notification.relatedEntityType,
      data: notification.metadata,
      timestamp: notification.createdAt,
      action: notification.action,
      read: notification.read,
    };
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Retourne un signal de l'état de connexion
   */
  isConnected(): boolean {
    return this.connectionState();
  }

  /**
   * Envoie un message à une destination WebSocket (ex: un endpoint @MessageMapping du backend).
   * @param destination Le topic de destination (ex: '/app/chat')
   * @param body L'objet à envoyer, sera sérialisé en JSON.
   */
  public sendMessage(destination: string, body: any): void {
    if (!this.stompClient?.connected) {
      console.error(
        '❌ [Alienala WebSocket] Cannot send message. Not connected.',
      );
      return;
    }

    console.log(
      `🚀 [Alienala WebSocket] Sending message to ${destination}:`,
      body,
    );
    this.stompClient.send(destination, {}, JSON.stringify(body));
  }
}
